/**
 * HTTP layer for the pitvisor API.
 *
 * Analyses do NOT come back on the request that asked for them: a cold
 * FastF1 load is 40-90 s and Cloudflare cuts proxied requests at 100 s.
 * So everything long-running goes through the job API — POST /job for an
 * id, GET /job/<id> until it finishes. See jobs.py on the server.
 */

export const BACKEND = process.env.REACT_APP_BACKEND || 'https://pitvisor-api.ojee.net';
export const LIVE_BASE = `${BACKEND}/live`;

async function request(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(`${BACKEND}${path}`, {
    method,
    signal,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) { /* non-JSON body */ }
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

/* ── inputs (cheap: Mongo-backed cascading dropdowns) ───────────────── */

export function fetchInputs(input, data, signal) {
  return request('/inputs', { method: 'POST', body: { input, data }, signal });
}

/* ── jobs ───────────────────────────────────────────────────────────── */

export function submitJob(funcName, inputList, signal) {
  return request('/job', { method: 'POST', body: { func_name: funcName, input_list: inputList }, signal });
}

export function getJob(id, signal) {
  return request(`/job/${id}`, { signal });
}

export function jobImageUrl(id) {
  return `${BACKEND}/image/${id}`;
}

/* ── replay player ──────────────────────────────────────────────────── */

export function listReplays(signal) {
  return request('/live/replays', { signal });
}

export function fetchReplaySchedule(year, signal) {
  return request(`/live/replays/schedule?year=${encodeURIComponent(year)}`, { signal });
}

export function downloadReplay(payload, signal) {
  return request('/live/replays/download', { method: 'POST', body: payload, signal });
}

export function startReplay(payload, signal) {
  return request('/live/replays/session/start', { method: 'POST', body: payload, signal });
}

export function stopReplay(sessionId, signal) {
  return request(`/live/replays/session/${encodeURIComponent(sessionId)}/stop`, { method: 'POST', signal });
}

export function replaySnapshot(sessionId, signal) {
  return request(`/live/replays/session/${encodeURIComponent(sessionId)}/snapshot`, { signal });
}

export function replayCommand(sessionId, command, payload, signal) {
  return request(`/live/replays/session/${encodeURIComponent(sessionId)}/${command}`, {
    method: 'POST',
    body: payload,
    signal,
  });
}

export function replayStreamUrl(sessionId) {
  return `${LIVE_BASE}/replays/session/${encodeURIComponent(sessionId)}/stream`;
}

export function replayTelemetryUrl(sessionId, drivers) {
  const list = (drivers || []).join(',');
  return `${LIVE_BASE}/replays/session/${encodeURIComponent(sessionId)}/telemetry/stream?drivers=${encodeURIComponent(list)}`;
}
